import { useRef } from "react";
import Image from "@tiptap/extension-image";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";

const MIN_W = 24;

function ImageView({ node, updateAttributes, selected, editor, getPos }) {
  const imgRef = useRef(null);
  const { src, alt, title, width, dx, dy } = node.attrs;

  const selectSelf = () => {
    if (typeof getPos === "function") {
      try { editor.commands.setNodeSelection(getPos()); } catch (_) { /* pos stale */ }
    }
  };

  const startResize = (dirX) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectSelf();
    const startX = e.clientX;
    const startW = imgRef.current?.offsetWidth || 120;
    const maxW = imgRef.current?.closest(".pgp-a4-sheet")?.clientWidth || 700;
    const onMove = (ev) => {
      let w = startW + dirX * (ev.clientX - startX);
      w = Math.max(MIN_W, Math.min(maxW, Math.round(w)));
      updateAttributes({ width: w });
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const startDrag = (e) => {
    if (e.target?.dataset?.handle) return;
    e.stopPropagation();
    selectSelf();
    const startX = e.clientX, startY = e.clientY;
    const ox = dx || 0, oy = dy || 0;
    const onMove = (ev) => {
      updateAttributes({ dx: ox + (ev.clientX - startX), dy: oy + (ev.clientY - startY) });
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const corners = [
    { key: "nw", x: -1, pos: { left: -6, top: -6, cursor: "nwse-resize" } },
    { key: "ne", x: 1, pos: { right: -6, top: -6, cursor: "nesw-resize" } },
    { key: "sw", x: -1, pos: { left: -6, bottom: -6, cursor: "nesw-resize" } },
    { key: "se", x: 1, pos: { right: -6, bottom: -6, cursor: "nwse-resize" } },
  ];

  return (
    <NodeViewWrapper
      as="span"
      className="pgp-img-node"
      draggable={false}
      style={{
        display: "inline-block",
        position: "relative",
        lineHeight: 0,
        transform: (dx || dy) ? `translate(${dx || 0}px, ${dy || 0}px)` : undefined,
        outline: selected ? "2px solid #f59e0b" : "none",
        outlineOffset: "1px",
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt || ""}
        title={title || ""}
        data-testid="editor-image"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onPointerDown={startDrag}
        style={{ width: width ? `${width}px` : "auto", height: "auto", maxWidth: "100%", display: "block", cursor: "move" }}
      />
      {selected && corners.map((c) => (
        <span
          key={c.key}
          data-handle={c.key}
          data-testid={`editor-image-handle-${c.key}`}
          onPointerDown={startResize(c.x)}
          style={{ position: "absolute", width: 12, height: 12, background: "#f59e0b", border: "2px solid #fff", borderRadius: 3, zIndex: 30, ...c.pos }}
        />
      ))}
    </NodeViewWrapper>
  );
}

export const ResizableImage = Image.extend({
  draggable: false,
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => {
          const w = el.style?.width || el.getAttribute("width");
          return w ? parseInt(w, 10) : null;
        },
        renderHTML: () => ({}),
      },
      dx: { default: 0, parseHTML: (el) => Number(el.getAttribute("data-dx")) || 0, renderHTML: () => ({}) },
      dy: { default: 0, parseHTML: (el) => Number(el.getAttribute("data-dy")) || 0, renderHTML: () => ({}) },
    };
  },
  renderHTML({ HTMLAttributes, node }) {
    const { width, dx, dy } = node.attrs;
    const style = [
      width ? `width:${width}px` : "",
      "height:auto",
      "max-width:100%",
      (dx || dy) ? `transform:translate(${dx || 0}px,${dy || 0}px)` : "",
    ].filter(Boolean).join(";");
    return ["img", { ...HTMLAttributes, style, "data-dx": dx || 0, "data-dy": dy || 0 }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageView);
  },
});
